import React, { useState, useEffect } from 'react';
import { Upload, X, Sparkles, Lock, Unlock, Share2, RotateCcw, ChevronRight, ChevronDown, Copy, Check, AlertTriangle, WifiOff, ArrowLeft } from 'lucide-react';
import { Clock, Star, DollarSign, Zap, Code, FileText } from 'lucide-react';
import ModelLogo, { getCategoryIcon } from './ModelLogo';
import { useModels } from '../hooks/useModels';
import { useCategories } from '../hooks/useCategories';
import { showToast } from './Toast';
import {
  trackBenchmarkRun,
  trackBenchmarkComplete,
  trackCategorySelect,
  trackModelSelect,
  trackPrivacyToggle,
  trackError
} from '../utils/GoogleAnalytics';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// ==========================================
// TIMEOUT CONFIGURATION BY CATEGORY
// ==========================================
const CATEGORY_TIMEOUTS = {
  'TEXT_TO_VIDEO': 360000,    // 6 minutes
  'IMAGE_TO_VIDEO': 360000,   // 6 minutes
  'IMAGE_GENERATION': 120000, // 2 minutes
  'CODE_GENERATION': 300000,  // 5 minutes - thinking models like GPT-5
  'CHAT': 300000,             // 5 minutes - thinking models
  'IMAGE_ANALYSIS': 180000,   // 3 minutes
  'DEFAULT': 120000           // 2 minutes fallback
};

const getTimeoutForCategory = (category) => {
  return CATEGORY_TIMEOUTS[category] || CATEGORY_TIMEOUTS.DEFAULT;
};

const formatTimeoutMessage = (category) => {
  const timeout = getTimeoutForCategory(category);
  const minutes = Math.floor(timeout / 60000);
  const seconds = (timeout % 60000) / 1000;

  if (minutes >= 1) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  return `${seconds} seconds`;
};

// ==========================================
// ERROR TYPE DETECTION
// ==========================================
const getErrorInfo = (error, category) => {
  if (!error) return null;

  const errorStr = error.message || error.toString() || '';
  const errorLower = errorStr.toLowerCase();

  // Timeout - credits NOT charged (request didn't complete)
  if (error.name === 'AbortError') {
    return {
      message: `Request timed out after ${formatTimeoutMessage(category)}. Your credits were NOT charged.`,
      icon: 'clock',
      color: 'orange'
    };
  }

  // Network errors - credits NOT charged
  if (errorLower.includes('failed to fetch') ||
      errorLower.includes('networkerror') ||
      errorLower.includes('network request failed')) {
    return {
      message: 'Connection lost. Please check your internet and try again. Your credits were NOT charged.',
      icon: 'wifi',
      color: 'orange'
    };
  }

  // Rate limit
  if (errorLower.includes('rate limit') || errorLower.includes('429')) {
    return {
      message: errorStr,
      icon: 'alert',
      color: 'red'
    };
  }

  // Default - show the actual error
  return {
    message: errorStr,
    icon: 'alert',
    color: 'red'
  };
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

const formatLatency = (ms) => {
  if (!ms || ms === 0) return '-';
  if (ms >= 1000) return (ms / 1000).toFixed(1) + 's';
  return Math.round(ms) + 'ms';
};

const formatCost = (cost) => {
  if (!cost || cost === 0) return '$0';
  if (cost < 0.001) return '<$0.001';
  if (cost < 0.01) return '$' + cost.toFixed(4);
  return '$' + cost.toFixed(3);
};

// Short category names for mobile display
const MOBILE_CATEGORY_NAMES = {
  'CODE_GENERATION': 'Code',
  'CHAT': 'Chat',
  'IMAGE_ANALYSIS': 'Analyze',
  'IMAGE_GENERATION': 'Images',
  'IMAGE_TO_VIDEO': 'Img→Vid',
  'TEXT_TO_VIDEO': 'Text→Vid',
};

// ==========================================
// IMAGE VALIDATION & RESIZE
// ==========================================
const IMAGE_LIMITS = {
  maxSizeMB: 10,
  maxSizeBytes: 10 * 1024 * 1024,
  maxDimension: 2048,
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  allowedExtensions: ['jpg', 'jpeg', 'png', 'webp', 'gif']
};

const validateImage = (file) => {
  // Check file type
  if (!IMAGE_LIMITS.allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Unsupported format. Please use: ${IMAGE_LIMITS.allowedExtensions.join(', ').toUpperCase()}`
    };
  }

  // Check file size
  if (file.size > IMAGE_LIMITS.maxSizeBytes) {
    return {
      valid: false,
      error: `Image too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size: ${IMAGE_LIMITS.maxSizeMB}MB`
    };
  }

  return { valid: true };
};

const resizeImageIfNeeded = (file, maxDimension = IMAGE_LIMITS.maxDimension) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Check if resize is needed
      if (img.width <= maxDimension && img.height <= maxDimension) {
        // No resize needed, read original file
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
        return;
      }

      // Calculate new dimensions
      let newWidth, newHeight;
      if (img.width > img.height) {
        newWidth = maxDimension;
        newHeight = Math.round((img.height / img.width) * maxDimension);
      } else {
        newHeight = maxDimension;
        newWidth = Math.round((img.width / img.height) * maxDimension);
      }

      // Create canvas and resize
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert to base64 (JPEG for smaller size)
      const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
      resolve(resizedDataUrl);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

// Get max prompt length from selected models (data comes from DB)
const getMaxPromptLength = (selectedModels) => {
  if (selectedModels.length === 0) return null;

  // Get limits from model data (max_prompt_length field from DB)
  const limits = selectedModels
    .map(m => m.max_prompt_length)
    .filter(limit => limit !== null && limit !== undefined);

  // If no models have limits (text models), return null
  if (limits.length === 0) return null;

  // Return the minimum limit among selected models
  return Math.min(...limits);
};

// ==========================================
// EXPANDABLE PROMPT COMPONENT
// ==========================================
function ExpandablePrompt({ prompt, maxLength = 100 }) {
  const [expanded, setExpanded] = React.useState(false);
  const needsExpansion = prompt && prompt.length > maxLength;

  const displayText = expanded || !needsExpansion
    ? prompt
    : prompt.substring(0, maxLength) + '...';

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-2 sm:p-3">
      <p className="text-sm font-medium text-gray-700 leading-relaxed break-words">
        {displayText || 'No prompt provided'}
        {needsExpansion && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-2 text-blue-600 hover:text-blue-800 font-semibold text-xs inline-flex items-center"
          >
            {expanded ? 'Show less' : 'Show more'}
            <ChevronRight
              size={14}
              className={`ml-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
            />
          </button>
        )}
      </p>
    </div>
  );
}

// ==========================================
// COPY BUTTON COMPONENT
// ==========================================
function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast('Copied to clipboard!', 'success');
    } catch (err) {
      showToast('Failed to copy', 'error');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`p-1.5 rounded-lg transition-all ${
        copied
          ? 'bg-green-100 text-green-600'
          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
      } ${className}`}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

// ==========================================
// STATS OVERLAY - Always visible on right
// ==========================================
function StatsOverlay({ results, category, isMobile = false }) {
  if (!results || results.length < 2) return null;

  const [result1, result2] = results;
  const model1Name = result1?.model || 'Model A';
  const model2Name = result2?.model || 'Model B';

  // Check if model failed
  const isModelFailed = (result) => {
    if (!result) return true;
    return !!result.error;
  };

  const model1Failed = isModelFailed(result1);
  const model2Failed = isModelFailed(result2);
  const anyFailed = model1Failed || model2Failed;

  // Get metrics
  const latency1 = result1?.latency_ms || 0;
  const latency2 = result2?.latency_ms || 0;
  const cost1 = result1?.cost || 0;
  const cost2 = result2?.cost || 0;

  // Determine winners (lower is better for speed and cost)
  const speedWinner = anyFailed ? 0 : (latency1 && latency2 ? (latency1 < latency2 ? 1 : latency2 < latency1 ? 2 : 0) : 0);
  const costWinner = anyFailed ? 0 : (cost1 && cost2 ? (cost1 < cost2 ? 1 : cost2 < cost1 ? 2 : 0) : 0);

  // Dynamic third metric based on category
  const getThirdMetric = () => {
    const isImageGen = category === 'IMAGE_GENERATION';
    const isVideoGen = category === 'IMAGE_TO_VIDEO' || category === 'VIDEO_GENERATION' || category === 'TEXT_TO_VIDEO';
    const isCodeGen = category === 'CODE_GENERATION';
    const isTranslation = category === 'MULTILINGUAL_TRANSLATION';

    if (isImageGen) {
      const res1 = result1?.resolution;
      const res2 = result2?.resolution;
      if (res1 || res2) {
        return { label: 'Size', value1: res1 || '-', value2: res2 || '-', winner: 0 };
      }
      // Fallback: status
      const status1 = result1?.image_url ? '✓' : '✗';
      const status2 = result2?.image_url ? '✓' : '✗';
      return { label: 'Status', value1: status1, value2: status2, winner: 0 };
    }

    if (isVideoGen) {
      const dur1 = result1?.video_duration;
      const dur2 = result2?.video_duration;
      return { label: 'Duration', value1: dur1 ? `${dur1}s` : '-', value2: dur2 ? `${dur2}s` : '-', winner: 0 };
    }

    if (isCodeGen) {
      const lines1 = result1?.code_lines;
      const lines2 = result2?.code_lines;
      const winner = anyFailed ? 0 : (lines1 && lines2 ? (lines1 > lines2 ? 1 : lines2 > lines1 ? 2 : 0) : 0);
      return { label: 'Lines', value1: lines1 != null ? lines1 : '-', value2: lines2 != null ? lines2 : '-', winner };
    }

    if (isTranslation) {
      const lang1 = result1?.detected_language?.toUpperCase()?.slice(0, 2);
      const lang2 = result2?.detected_language?.toUpperCase()?.slice(0, 2);
      if (lang1 || lang2) {
        return { label: 'Lang', value1: lang1 || '-', value2: lang2 || '-', icon: 'lang', winner: 0 };
      }
    }
    // Default: word count from DB
    const words1 = result1?.word_count;
    const words2 = result2?.word_count;
    const winner = anyFailed ? 0 : (words1 && words2 ? (words1 > words2 ? 1 : words2 > words1 ? 2 : 0) : 0);
    return { label: 'Words', value1: words1 != null ? words1 : '-', value2: words2 != null ? words2 : '-', winner };
  };

  const thirdMetric = getThirdMetric();
  const thirdMetricWinner = thirdMetric?.winner || 0;

  // Cell styling
  const getCellStyle = (isWinner, isFailed = false) => {
    if (isFailed) return 'text-red-400';
    if (isWinner) return 'bg-green-100 text-green-700';
    return 'text-gray-600';
  };

  // Mobile version - table above cards
  if (isMobile) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-3">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="py-2 px-2 text-left w-24"></th>
              <th className="py-2 px-1 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap size={12} className="text-amber-500" />
                  <span className="text-[10px] text-gray-500 font-semibold">SPEED</span>
                </div>
              </th>
              <th className="py-2 px-1 text-center">
                <div className="flex items-center justify-center gap-1">
                  <DollarSign size={12} className="text-green-500" />
                  <span className="text-[10px] text-gray-500 font-semibold">COST</span>
                </div>
              </th>
              {thirdMetric && (
                <th className="py-2 px-1 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Code size={12} className="text-blue-500" />
                    <span className="text-[10px] text-gray-500 font-semibold">{thirdMetric.label.toUpperCase()}</span>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-gray-50/50">
              <td className="py-2.5 px-2">
                <div className="flex items-center gap-1.5">
                  <ModelLogo modelName={model1Name} size="xs" />
                  <span className={`text-xs font-medium truncate max-w-[60px] ${model1Failed ? 'text-red-400' : 'text-gray-700'}`}>
                    {model1Name.split(' ')[0]}
                  </span>
                </div>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(speedWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : formatLatency(latency1)}
                </span>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(costWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : formatCost(cost1)}
                </span>
              </td>
              {thirdMetric && (
                <td className="py-2.5 px-1 text-center">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 1, model1Failed)}`}>
                    {model1Failed ? '-' : thirdMetric.value1}
                  </span>
                </td>
              )}
            </tr>
            <tr>
              <td className="py-2.5 px-2">
                <div className="flex items-center gap-1.5">
                  <ModelLogo modelName={model2Name} size="xs" />
                  <span className={`text-xs font-medium truncate max-w-[60px] ${model2Failed ? 'text-red-400' : 'text-gray-700'}`}>
                    {model2Name.split(' ')[0]}
                  </span>
                </div>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(speedWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : formatLatency(latency2)}
                </span>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(costWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : formatCost(cost2)}
                </span>
              </td>
              {thirdMetric && (
                <td className="py-2.5 px-1 text-center">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 2, model2Failed)}`}>
                    {model2Failed ? '-' : thirdMetric.value2}
                  </span>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Desktop version - floating on right
  return (
    <div
      className="absolute right-0 w-[160px] z-10 pointer-events-auto hidden sm:block"
      style={{
        background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1) 15%)',
        top: '12px',
        bottom: '12px'
      }}
    >
      <div className="ml-4 h-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden flex flex-col">
        {/* Header with model icons */}
        <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0">
          <div className="flex justify-around items-center">
            <div className={model1Failed ? 'opacity-50' : ''}>
              <ModelLogo modelName={model1Name} size="sm" />
            </div>
            <div className={model2Failed ? 'opacity-50' : ''}>
              <ModelLogo modelName={model2Name} size="sm" />
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="flex-1 flex flex-col divide-y divide-gray-100">
          {/* Speed */}
          <div className="flex-1 p-2 flex flex-col justify-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Zap size={14} className="text-amber-500" />
              <span className="text-xs text-gray-500 font-semibold uppercase">Speed</span>
            </div>
            <div className="flex justify-around">
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(speedWinner === 1, model1Failed)}`}>
                {model1Failed ? '-' : formatLatency(latency1)}
              </span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(speedWinner === 2, model2Failed)}`}>
                {model2Failed ? '-' : formatLatency(latency2)}
              </span>
            </div>
          </div>

          {/* Cost */}
          <div className="flex-1 p-2 flex flex-col justify-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <DollarSign size={14} className="text-green-500" />
              <span className="text-xs text-gray-500 font-semibold uppercase">Cost</span>
            </div>
            <div className="flex justify-around">
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(costWinner === 1, model1Failed)}`}>
                {model1Failed ? '-' : formatCost(cost1)}
              </span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(costWinner === 2, model2Failed)}`}>
                {model2Failed ? '-' : formatCost(cost2)}
              </span>
            </div>
          </div>

          {/* Third Metric */}
          {thirdMetric && (
            <div className="flex-1 p-2 flex flex-col justify-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Code size={14} className="text-blue-500" />
                <span className="text-xs text-gray-500 font-semibold uppercase">{thirdMetric.label}</span>
              </div>
              <div className="flex justify-around">
                <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : thirdMetric.value1}
                </span>
                <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : thirdMetric.value2}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * BenchmarkRunner - Main comparison interface
 */
// ==========================================
// PROVIDER ACCORDION — Company → Models
// ==========================================
function ProviderAccordion({ models, selectedModels, onToggle }) {
  const [expandedProvider, setExpandedProvider] = React.useState(null);

  const grouped = {};
  models.forEach(model => {
    const provider = model.provider || 'Other';
    if (!grouped[provider]) grouped[provider] = [];
    grouped[provider].push(model);
  });

  const providers = Object.keys(grouped).sort((a, b) => {
    const aSelected = grouped[a].some(m => selectedModels.find(s => s.id === m.id));
    const bSelected = grouped[b].some(m => selectedModels.find(s => s.id === m.id));
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {providers.map(provider => {
          const providerModels = grouped[provider];
          const selectedCount = providerModels.filter(m => selectedModels.find(s => s.id === m.id)).length;
          const isExpanded = expandedProvider === provider;
          const hasNew = providerModels.some(m => m.tag === 'NEW' && !m.locked);

          return (
            <button
              key={provider}
              onClick={() => setExpandedProvider(isExpanded ? null : provider)}
              className={`relative flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${
                isExpanded
                  ? 'bg-blue-50 border-blue-400 shadow-md'
                  : selectedCount > 0
                    ? 'bg-white border-blue-300 shadow-sm'
                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <ModelLogo modelName={providerModels[0]?.name || provider} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-900 truncate">{provider}</div>
                <div className="text-xs text-gray-400">{providerModels.length} model{providerModels.length > 1 ? 's' : ''}</div>
              </div>
              {selectedCount > 0 && (
                <span className="bg-blue-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                  {selectedCount}
                </span>
              )}
              {hasNew && selectedCount === 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
              )}
              <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          );
        })}
      </div>

      {expandedProvider && grouped[expandedProvider] && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-3 space-y-1.5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{expandedProvider} Models</span>
            <span className="text-xs text-gray-400">{grouped[expandedProvider].length} available</span>
          </div>
          {grouped[expandedProvider].map(model => {
            const isSelected = selectedModels.find(m => m.id === model.id);
            const isLocked = model.locked;
            const selIndex = selectedModels.findIndex(m => m.id === model.id);

            return (
              <button
                key={model.id}
                onClick={() => onToggle(model)}
                disabled={false}
                className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-left ${
                  isSelected ? 'bg-white shadow-sm' : 'hover:bg-white/80'
                }`}
              >
                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                  isSelected ? 'bg-blue-500 border-transparent text-white' : 'border-gray-300 bg-white'
                }`}>
                  {isSelected && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>

                <ModelLogo model={model} size="sm" showNew={false} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium text-sm ${isLocked ? 'text-gray-400' : 'text-gray-900'}`}>{model.name}</span>
                    {model.tag === 'NEW' && !isLocked && (
                      <span className="bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
                    )}
                  </div>
                </div>

                <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                  isLocked ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600'
                }`}>
                  {model.credits_per_run || 1} cr
                </span>

                {isLocked && (
                  <div className={`p-1 rounded-full flex-shrink-0 ${
                    model.required_tier === 'starter' ? 'bg-gradient-to-r from-amber-600 to-orange-700' :
                    model.required_tier === 'pro' ? 'bg-gradient-to-r from-gray-400 to-gray-500' :
                    'bg-gradient-to-r from-yellow-400 to-yellow-600'
                  }`}>
                    <Lock size={10} className="text-white" />
                  </div>
                )}

                {isSelected && (
                  <span className="bg-blue-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                    {selIndex + 1}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BenchmarkRunner({ user, onCreditsUpdate, onBenchmarkComplete }) {
  const { categories, loading: categoriesLoading } = useCategories();
  const { models: availableModels, loading: modelsLoading } = useModels();

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedModels, setSelectedModels] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [image, setImage] = useState(null);
  const [running, setRunning] = useState(false);
  const [waitTime, setWaitTime] = useState(0);
  const [estimatedCredits, setEstimatedCredits] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [errorInfo, setErrorInfo] = useState(null);
  const [isPublic, setIsPublic] = useState(false);

  // Calculate prompt limit and validation (from DB via model data)
  const maxPromptLength = getMaxPromptLength(selectedModels);
  const isPromptTooLong = maxPromptLength && prompt.length > maxPromptLength;
  const hasAnyFailedResult = results?.results?.some(r => r.error);

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      const firstCategory = categories[0];
      setSelectedCategory(firstCategory.id);
      setPrompt(firstCategory.description || '');
    }
  }, [categories]);

  // Track wait time while running
  useEffect(() => {
    let timer;
    if (running) {
      setWaitTime(0);
      timer = setInterval(() => {
        setWaitTime(prev => prev + 1);
      }, 1000);
    } else {
      setWaitTime(0);
    }
    return () => clearInterval(timer);
  }, [running]);

  // Calculate credit range for a category
  const getCreditRangeForCategory = (categoryId) => {
    if (!availableModels || availableModels.length === 0) return null;

    // Filter models by exact category match from DB
    const categoryModels = availableModels.filter(model => {
      return model.category === categoryId;
    });

    if (categoryModels.length === 0) return null;

    const credits = categoryModels.map(m => m.credits_per_run || 1);
    const min = Math.min(...credits);
    const max = Math.max(...credits);

    return min === max ? `${min} credit${min !== 1 ? 's' : ''}` : `${min}-${max} credits`;
  };

  const getModelsForCategory = () => {
    if (!availableModels) return [];

    // Filter models by exact category match from DB
    return availableModels.filter(model => {
      return model.category === selectedCategory;
    });
  };

  const categoryModels = getModelsForCategory();

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedModels([]);
    setEstimatedCredits(0);
    setResults(null);
    setError('');
    setErrorInfo(null);
    const category = categories.find(c => c.id === categoryId);
    if (category) setPrompt(category.description || '');
    trackCategorySelect(categoryId);
  };

  const toggleModel = (model) => {
    // PORTFOLIO: locked check removed — allow selecting any model in demo mode

    const isSelected = selectedModels.find(m => m.id === model.id);

    if (isSelected) {
      setSelectedModels(selectedModels.filter(m => m.id !== model.id));
      setError('');
    } else {
      if (selectedModels.length >= 2) {
        setError('⚠️ Maximum 2 models allowed');
        setTimeout(() => setError(''), 3000);
        return;
      }

      const userCredits = user?.credits || 0;
      const modelCredits = model.credits_per_run || 1;

      if (userCredits < modelCredits) {
        setError(`Not enough credits. Need ${modelCredits}.`);
        setTimeout(() => setError(''), 3000);
        return;
      }

      setSelectedModels([...selectedModels, model]);
      setError('');
      trackModelSelect(model.id, selectedCategory);
    }
  };

  useEffect(() => {
    const total = selectedModels.reduce((sum, model) => sum + (model.credits_per_run || 1), 0);
    setEstimatedCredits(total);
  }, [selectedModels]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate image
    const validation = validateImage(file);
    if (!validation.valid) {
      setError(validation.error);
      showToast(validation.error, 'error');
      e.target.value = ''; // Reset input
      return;
    }

    try {
      // Resize if needed and convert to base64
      const resizedImage = await resizeImageIfNeeded(file);
      setImage(resizedImage);
      setError(''); // Clear any previous errors

      // Show success message if resized
      const originalSize = (file.size / 1024 / 1024).toFixed(1);
      if (file.size > 1024 * 1024) { // If original was > 1MB
        showToast(`Image ready (${originalSize}MB → optimized)`, 'success');
      }
    } catch (err) {
      setError('Failed to process image. Please try another file.');
      showToast('Failed to process image', 'error');
      e.target.value = '';
    }
  };

  const handleRun = async () => {
    setError('');
    setErrorInfo(null);

    // Check prompt length
    if (isPromptTooLong) {
      setError(`Prompt too long! Max ${maxPromptLength} characters for selected models.`);
      return;
    }

    if (!user) {
      if (selectedModels.length > 1) {
        setError('Guest users can only run 1 model. Please login.');
        return;
      }
      if (selectedCategory === 'IMAGE_GENERATION' || selectedCategory === 'IMAGE_TO_VIDEO' || selectedCategory === 'TEXT_TO_VIDEO') {
        setError('Image/Video generation requires login.');
        return;
      }
    }

    if (selectedModels.length === 0) {
      setError('Please select at least one model');
      return;
    }

    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    const needsImage = selectedCategory === 'IMAGE_ANALYSIS' || selectedCategory === 'IMAGE_TO_VIDEO';
    if (needsImage && !image) {
      setError('Please upload an image');
      return;
    }

    setRunning(true);
    setResults(null);

    trackBenchmarkRun(selectedCategory, selectedModels.map(m => m.id), estimatedCredits);

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeout = getTimeoutForCategory(selectedCategory);
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const response = await fetch(`${API_URL}/benchmark/run`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          user_email: user?.email,
          category: selectedCategory,
          prompt,
          models: selectedModels.map(m => m.id),
          image: image,
          is_public: false
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to run benchmark');
      }

      setResults(data);
      setIsPublic(false);

      trackBenchmarkComplete(selectedCategory, selectedModels.length, data.run_id);
      // Refresh credits display
      if (onCreditsUpdate) {
        onCreditsUpdate();
      }
      // Trigger feedback widget
      if (onBenchmarkComplete) {
        onBenchmarkComplete();
      }
      if (data.failed_models && data.failed_models.length > 0) {
        const refundedCredits = data.credit_summary?.refunded || 0;
        showToast(
          `⚠️ ${data.failed_models.length} model(s) failed. ${refundedCredits} credits refunded.`,
          'warning',
          5000
        );
      }

      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);

    } catch (err) {
      clearTimeout(timeoutId);

      // Get user-friendly error info
      const info = getErrorInfo(err, selectedCategory);
      setErrorInfo(info);
      setError(info?.message || err.message || 'An error occurred');

      trackError('benchmark_failed', err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleTogglePublic = async () => {
    if (!results?.run_id || !user) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/benchmark/${results.run_id}/privacy`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_public: !isPublic })
        }
      );

      if (response.ok) {
        const newStatus = !isPublic;
        setIsPublic(newStatus);
        showToast(
          isPublic ? '🔒 Result is now private' : '🌍 Result is now public!',
          'success'
        );
        trackPrivacyToggle(results.run_id, newStatus);
      }
    } catch (error) {
      console.error('Privacy toggle error:', error);
    }
  };

  // ==========================================
  // RENDER: RESULTS
  // ==========================================

    if (results) {
       const creditSummary = results.credit_summary;
       const canShare = !hasAnyFailedResult; // Can only share if all models succeeded

    return (
      <div id="results-section" className="max-w-6xl mx-auto px-4 sm:px-6 pt-3 space-y-3">

        {/* Header Row */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center flex-shrink-0 shadow-sm">
                {getCategoryIcon(selectedCategory, 20)}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-bold text-gray-900 truncate">
                  {selectedCategory?.replace(/_/g, ' ') || 'Comparison'}
                </h2>
                <p className="text-sm text-gray-500 truncate">{prompt}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 flex-shrink-0">
              {user && canShare && (
                <button
                  onClick={handleTogglePublic}
                  className={`flex items-center space-x-1.5 px-3 py-2 rounded-xl font-medium text-sm transition-all ${
                    isPublic
                      ? 'bg-blue-50 border border-blue-200 text-blue-700'
                      : 'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                  aria-label={isPublic ? 'Make comparison private' : 'Make comparison public'}
                  aria-pressed={isPublic}
                >
                  {isPublic ? <Unlock size={16} aria-hidden="true" /> : <Lock size={16} aria-hidden="true" />}
                  <span className="hidden sm:inline">{isPublic ? 'Public' : 'Private'}</span>
                </button>
              )}
              <button
                onClick={() => { setResults(null); setSelectedModels([]); }}
                className="flex items-center space-x-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium text-sm"
                aria-label="Start new comparison"
              >
                <RotateCcw size={16} aria-hidden="true" />
                <span className="hidden sm:inline">New</span>
              </button>
            </div>
          </div>
        </div>

        {/* Credit Summary Alert */}
        {creditSummary && creditSummary.refunded > 0 && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
            <p className="text-sm text-yellow-800">
              💰 <strong>{creditSummary.refunded}</strong> credits refunded.
              Charged: <strong>{creditSummary.charged}</strong> |
              Balance: <strong>{creditSummary.final_balance}</strong>
            </p>
          </div>
        )}

        {/* Mobile Stats */}
        {results.results && results.results.length >= 2 && (
          <div className="sm:hidden">
            <StatsOverlay results={results.results} category={selectedCategory} isMobile={true} />
          </div>
        )}

        {/* Results Grid */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4 relative">
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${results.results?.length >= 2 ? 'sm:pr-[170px]' : ''}`}>
           {results.results?.map((result, idx) => {

              const hasError = !!result.error;

              return (
                <article
                  key={result.id || idx}
                  className={`flex flex-col bg-white rounded-xl border-2 shadow-sm transition-all overflow-hidden ${
                    hasError ? 'border-red-200' : 'border-gray-100'
                  }`}
                >
                  {/* Model Header */}
                  <div className={`flex items-center justify-between p-3 border-b ${
                    hasError ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'
                  }`}>
                    <div className="flex items-center space-x-2">
                      <ModelLogo modelName={result.model} size="sm" />
                      <h3 className="font-bold text-sm text-gray-900">{result.model}</h3>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                      hasError ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {hasError ? '0 cr (refund)' : `${result.credits_charged || 1} cr`}
                    </span>
                  </div>

                {/* Content */}
                <div className="p-3" style={{ height: '220px' }}>
                  {hasError ? (
                    <div className="bg-red-50 rounded-xl p-4 h-full flex flex-col justify-center">
                      <p className="text-sm font-semibold text-red-700 mb-1">❌ Error</p>
                      <p className="text-xs text-red-600">{result.error}</p>
                    </div>
                  ) : result.video_url ? (
                    <div className="relative rounded-xl overflow-hidden h-full bg-black flex items-center justify-center">
                      <video
                        src={result.video_url}
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-contain"
                      >
                        Your browser does not support video playback.
                      </video>
                    </div>
                  ) : result.image_url ? (
                    <div className="relative rounded-xl overflow-hidden h-full">
                      <img
                        src={result.image_url}
                        alt={`AI-generated image by ${result.model}`}
                        className="w-full h-full object-cover"
                        width={400}
                        height={220}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  ) : (
                    <div className="relative bg-gray-900 rounded-xl h-full overflow-hidden">
                      <CopyButton
                        text={result.response || result.output || ''}
                        className="absolute top-2 right-2 z-10"
                      />
                      <pre className="text-xs text-gray-100 p-3 h-full overflow-y-auto whitespace-pre-wrap break-words font-mono leading-relaxed">
                        {result.response || result.output || 'No response'}
                      </pre>
                    </div>
                  )}
                </div>
                </article>
              );
            })}
          </div>

          {/* Desktop Stats Overlay - Always visible */}
          {results.results && results.results.length >= 2 && (
            <StatsOverlay results={results.results} category={selectedCategory} />
          )}
        </div>

        {/* Bottom Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              {creditSummary && (
                <span>
                  Used: <strong className="text-gray-900">{creditSummary.charged}</strong> |
                  Balance: <strong className="text-gray-900">{creditSummary.final_balance}</strong>
                </span>
              )}
            </div>

            {/* Share button - only show if all models succeeded */}
            {user && canShare && !isPublic && (
              <button
                onClick={handleTogglePublic}
                className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all text-sm"
                aria-label="Share comparison with community"
              >
                <Share2 size={16} aria-hidden="true" />
                <span>Share with Community</span>
              </button>
            )}

            {/* Warning message if cannot share */}
            {user && !canShare && (
              <div className="flex items-center space-x-2 px-4 py-2.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl text-sm" role="alert">
                <AlertTriangle size={16} aria-hidden="true" />
                <span>Can't share: A model failed. Try again or choose different models.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER: MAIN UI
  // ==========================================

  // Helper function to render error icon
  const renderErrorIcon = () => {
    if (!errorInfo) return <AlertTriangle size={16} />;
    switch (errorInfo.icon) {
      case 'clock': return <Clock size={16} />;
      case 'wifi': return <WifiOff size={16} />;
      default: return <AlertTriangle size={16} />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-3 space-y-3">
      {/* Category Selection */}
      <section className="bg-white rounded-2xl p-4 md:p-5 border border-gray-100 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center space-x-2">
          <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
          <span>Select Category</span>
        </h2>

        {categoriesLoading || modelsLoading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" role="group" aria-label="Select category">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`p-3 rounded-xl transition-all text-left ${
                  selectedCategory === cat.id
                    ? 'border-2 border-blue-500 bg-blue-50 shadow-md'
                    : 'border border-gray-100 bg-white shadow-sm hover:shadow-md'
                }`}
                aria-pressed={selectedCategory === cat.id}
                aria-label={`Select ${cat.name} category`}
              >
                <div className="flex items-center space-x-2">
                  <span aria-hidden="true">{getCategoryIcon(cat.id, 16)}</span>
                  <span className="font-semibold text-xs sm:text-base text-gray-800">
                    <span className="hidden sm:inline">{cat.name}</span>
                    <span className="sm:hidden">{MOBILE_CATEGORY_NAMES[cat.id] || cat.name}</span>
                  </span>
                </div>
                {getCreditRangeForCategory(cat.id) && (
                  <div className="text-xs text-gray-400 mt-1">{getCreditRangeForCategory(cat.id)}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Error Alert - Show at top for immediate feedback */}
      {error && (
        <div className={`rounded-xl p-3 text-sm flex items-center space-x-2 ${
          errorInfo?.color === 'orange'
            ? 'bg-orange-50 border border-orange-200 text-orange-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {renderErrorIcon()}
          <span>{error}</span>
        </div>
      )}

      {/* Model Selection — Accordion by Provider */}
      <section className="bg-white rounded-2xl p-4 md:p-5 border border-gray-100 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center space-x-2">
          <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
          <span>Select Models (Max 2)</span>
          {selectedModels.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full font-bold">{selectedModels.length}/2</span>
          )}
        </h2>

        {modelsLoading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : categoryModels.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm">No models available</div>
        ) : (
          <ProviderAccordion
            models={categoryModels}
            selectedModels={selectedModels}
            onToggle={toggleModel}
          />
        )}
      </section>

      {/* Image Upload */}
      {(selectedCategory === 'IMAGE_ANALYSIS' || selectedCategory === 'IMAGE_TO_VIDEO') && (
        <section className="bg-white rounded-2xl p-4 md:p-5 border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center space-x-2">
            <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-lg flex items-center justify-center text-sm">📸</span>
            <span>Upload Image</span>
          </h2>
          {image ? (
            <div className="relative inline-block">
              <img src={image} alt="Uploaded image preview" className="max-w-xs rounded-xl border shadow-sm" />
              <button
                onClick={() => setImage(null)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                aria-label="Remove uploaded image"
              >
                <X size={12} aria-hidden="true" />
              </button>
            </div>
          ) : (
            <label
              className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.currentTarget.querySelector('input')?.click();
                }
              }}
            >
              <Upload size={28} className="text-gray-300 mb-1" aria-hidden="true" />
              <span className="text-gray-500 text-xs">Click to upload</span>
              <span className="text-gray-400 text-[10px] mt-1">JPG, PNG, WebP • Max 10MB</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                className="hidden"
                aria-label="Upload image file"
              />
            </label>
          )}
        </section>
      )}

      {/* Prompt Input */}
      <section className="bg-white rounded-2xl p-4 md:p-5 border border-gray-100 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center space-x-2">
          <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-lg flex items-center justify-center text-sm font-bold">3</span>
          <span>Enter Prompt</span>
        </h2>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Type your prompt..."
          className={`w-full h-20 p-3 border rounded-xl focus:ring-2 resize-none text-gray-700 text-sm transition-colors ${
            isPromptTooLong
              ? 'border-red-400 focus:border-red-400 focus:ring-red-50 bg-red-50'
              : 'border-gray-200 focus:border-blue-400 focus:ring-blue-50'
          }`}
        />

        {/* Character counter - only show for categories with limits */}
        {maxPromptLength && (
          <div className={`flex items-center justify-between mt-2 text-xs ${
            isPromptTooLong ? 'text-red-500' : 'text-gray-400'
          }`}>
            <div className="flex items-center space-x-1">
              {isPromptTooLong && (
                <>
                  <AlertTriangle size={12} />
                  <span>Prompt too long for selected models!</span>
                </>
              )}
            </div>
            <span className={`font-medium ${isPromptTooLong ? 'text-red-600' : ''}`}>
              {prompt.length.toLocaleString()} / {maxPromptLength.toLocaleString()} characters
            </span>
          </div>
        )}
      </section>


      {/* Run Button */}
      <section className="bg-white rounded-2xl p-4 md:p-5 border border-gray-100 shadow-sm">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-center sm:text-left">
            <p className="text-sm text-gray-500"><strong>{selectedModels.length}/2</strong> models selected</p>
            <p className="text-lg font-bold text-gray-900">{estimatedCredits} credits</p>
            {user && <p className="text-xs text-gray-500">Balance: {user.credits || 0}</p>}
          </div>

          <button
            onClick={handleRun}
            disabled={true}
            title="Demo mode — running disabled"
            className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold transition-all bg-gradient-to-r from-blue-600 to-purple-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🚀 Run Comparison
          </button>
        </div>
      </section>
    </div>
  );
}

export default BenchmarkRunner;